import { useEffect, useMemo, useRef, useState } from 'react';
import { apiClient, axiosClient } from '../auth-lib';
import { useNavigate, useLocation } from 'react-router-dom';
import OrgAdminLayout from '../org-admin/components/OrgAdminLayout';
import { Button, Card, Input, Select, useToast } from '../design-system';
import { MONTHS } from '../lib/serviceCatalog';
import '../styles/CreateTask.css';

// Ordinal + "months after period" phrasing for the recurrence due-date preview.
const ordinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};
const MONTH_BASED = ['monthly', 'quarterly', 'half_yearly', 'yearly'];
// Frequencies where the user picks ONE anchor month and the rest of the year's
// runs are derived from the statutory cadence (every 3 / 6 months). Yearly is a
// single-month anchor (no derived siblings) — handled separately.
const ANCHOR_STEP = { quarterly: 3, half_yearly: 6 };
// Weeks-of-month chips for a weekly schedule: 1st–4th plus a "Last" bucket
// (5) that resolves to whatever the final week of a given month is, so a
// "last week" schedule works in months with a 5th week too.
const WEEK_LABELS = [
  { no: 1, label: '1st' },
  { no: 2, label: '2nd' },
  { no: 3, label: '3rd' },
  { no: 4, label: '4th' },
  { no: 5, label: 'Last' },
];
const CURRENT_YEAR = new Date().getFullYear();
const CURRENT_MONTH = new Date().getMonth() + 1;
// A window of years the yearly schedule can be pinned to.
const YEAR_OPTIONS = Array.from({ length: 8 }, (_, i) => CURRENT_YEAR + i);

// Given an anchor month (1-12) and a step, return every month in a calendar
// year that lands on that cadence, wrapping around December. Quarterly from
// Nov → {Nov, Feb, May, Aug}. Half-yearly from Mar → {Mar, Sep}.
const anchoredMonths = (anchor, step) => {
  const set = new Set();
  for (let k = 0; k < 12 / step; k += 1) {
    set.add(((anchor - 1 + k * step) % 12) + 1);
  }
  return [...set].sort((a, b) => a - b);
};

// Same title-case formatter used on the Services page — the catalog is
// SCREAMING_CASE throughout, so it needs the same acronym-aware cleanup
// here or every option in this dropdown would just shout.
const SMALL_WORDS = new Set(['of', 'in', 'by', 'to', 'and', 'on', 'at', 'for', 'the', 'a', 'an', 'or', 'from', 'with', 'within']);
const KEEP_UPPER = new Set([
  'tds', 'pan', 'tan', 'itr', 'roc', 'llp', 'din', 'fema', 'lodr', 'nclt', 'aoa', 'moa', 'rd',
  'iec', 'pt', 'ec', 'gst', 'gstr', 'cit', 'itat', 'fc', 'gpr', 'tc', 'trs', 'fla', 'odi',
  'ptec', 'ptrc', 'lut', 'sez', 'reg', 'cr', 'ii', 'i',
]);
const titleCaseWord = (word, isFirst) => {
  const lower = word.toLowerCase();
  const bare = lower.replace(/[^a-z]/g, '');
  if (KEEP_UPPER.has(bare)) return word.toUpperCase();
  if (!isFirst && SMALL_WORDS.has(lower)) return lower;
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};
const formatServiceName = (raw) =>
  raw
    .split(' ')
    .map((token, idx) => {
      if (/[\d()@./]/.test(token)) return token;
      if (token.includes('-')) return token.split('-').map((p) => titleCaseWord(p, false)).join('-');
      return titleCaseWord(token, idx === 0);
    })
    .join(' ');

function CreateTask({ Layout = OrgAdminLayout, backPath = '/org-admin/tasks' }) {
  // A "Clone task" navigation arrives with the source task's initial-stage
  // fields on location.state.clone — they override the blank defaults below so
  // the user lands on a pre-filled form to edit and submit (nothing is created
  // until they hit Create).
  const location = useLocation();
  const [formData, setFormData] = useState(() => ({
    title: '',
    description: '',
    priority: 'medium',
    dueDate: '',
    tags: [],
    clientIds: [],
    preparerId: '',
    preparerHours: '',
    reviewerId: '',
    reviewerHours: '',
    assistantIds: [],
    assistantHours: {},
    isBillable: false,
    billType: 'professional_fees',
    billAmount: '',
    // Which catalog service (if any) seeded the title. Picking one hides the
    // recurrence block — a service already recurs via its own due-rule.
    selectedServiceId: '',
    selectedServiceName: '',
    // Recurrence (ad-hoc tasks only). Mutually exclusive with a picked service.
    recurring: false,
    recurrenceFrequency: 'monthly',
    recurrenceInterval: '1',
    recurrenceEndType: 'never',
    recurrenceEndDate: '',
    recurrenceOccurrences: '',
    // Which calendar months (1-12) it runs in — lets the user skip months.
    // For quarterly/half-yearly/yearly this is DERIVED from the anchor month
    // below; only monthly toggles all twelve freely.
    recurrenceMonths: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
    // The single month the user picks for quarterly/half-yearly/yearly. The
    // sibling months (quarterly/half-yearly) are computed from it.
    recurrenceAnchorMonth: CURRENT_MONTH,
    // Which weeks of the month a weekly schedule fires in (1-4, 5 = "Last").
    recurrenceWeeks: [1, 2, 3, 4, 5],
    // Which specific years a yearly schedule runs in.
    recurrenceYears: [CURRENT_YEAR],
    // Due-date rule for each occurrence. Mode: 'none' | 'day' | 'month_end'.
    recurrenceDueMode: 'none',
    recurrenceDueDay: '',
    recurrenceMonthOffset: '1',
    // Clone prefill (subset of the keys above) wins over the blank defaults.
    ...(location.state?.clone || {}),
  }));
  const [orgUsers, setOrgUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [services, setServices] = useState([]);
  const [serviceSearch, setServiceSearch] = useState('');
  const [servicePickerOpen, setServicePickerOpen] = useState(false);
  // Two precise refs (the input itself and the results box) rather than
  // their wrapping <div> — that wrapper is a normal block element and
  // spans the full form width even though the input only renders at
  // 340px, so clicking the blank space next to the input was being
  // counted as "inside" and failing to close the picker.
  const serviceInputRef = useRef(null);
  const serviceBoxRef = useRef(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    axiosClient.get('/users').then((res) => setOrgUsers(res.data)).catch(() => {});
    apiClient.get('/api/services').then((res) => setServices(res.data)).catch(() => {});
    apiClient.get('/api/clients').then((res) => setClients(res.data)).catch(() => {});
  }, []);

  // A clone prefill carries the source task's serviceId but not its display
  // name (the catalog is SCREAMING_CASE and needs the formatter below), so
  // resolve the chip label once the catalog has loaded.
  useEffect(() => {
    if (!formData.selectedServiceId || formData.selectedServiceName || !services.length) return;
    const match = services.find((s) => s._id === formData.selectedServiceId);
    if (match) {
      setFormData((prev) => ({ ...prev, selectedServiceName: formatServiceName(match.name) }));
    }
  }, [services, formData.selectedServiceId, formData.selectedServiceName]);

  // Standard combobox behavior: open on focus, close on an outside click.
  useEffect(() => {
    if (!servicePickerOpen) return undefined;
    const handleClickOutside = (e) => {
      const insideInput = serviceInputRef.current && serviceInputRef.current.contains(e.target);
      const insideBox = serviceBoxRef.current && serviceBoxRef.current.contains(e.target);
      if (!insideInput && !insideBox) {
        setServicePickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [servicePickerOpen]);

  // Grouped by category so the picker reads as category > sub-category,
  // matching how the Services page and the client Enroll form group them.
  // A plain scrollable box (not a native <select>) on purpose — a native
  // select's dropdown popup is positioned by the browser itself, which
  // was shifting the whole page to fit this list's 80+ options.
  const serviceGroups = useMemo(() => {
    const term = serviceSearch.trim().toLowerCase();
    const enabledServices = services.filter((s) => s.isEnabled !== false);
    const filtered = term
      ? enabledServices.filter(
          (s) => s.name.toLowerCase().includes(term) || s.groupName.toLowerCase().includes(term)
        )
      : enabledServices;

    const byGroup = new Map();
    filtered.forEach((s) => {
      const key = s.groupName.trim().toLowerCase();
      if (!byGroup.has(key)) byGroup.set(key, { displayName: s.groupName, items: [] });
      byGroup.get(key).items.push(s);
    });
    return Array.from(byGroup.values()).sort((a, b) => b.items.length - a.items.length);
  }, [services, serviceSearch]);

  const handleServiceSelect = (service) => {
    const name = formatServiceName(service.name);
    setFormData((prev) => ({
      ...prev,
      title: name,
      selectedServiceId: service._id,
      selectedServiceName: name,
      // A service and a manual recurrence are mutually exclusive.
      recurring: false,
    }));
    setServicePickerOpen(false);
    setServiceSearch('');
  };

  const clearSelectedService = () => {
    setFormData((prev) => ({ ...prev, selectedServiceId: '', selectedServiceName: '' }));
  };

  const handleRecurringToggle = (e) => {
    const checked = e.target.checked;
    setFormData((prev) => ({
      ...prev,
      recurring: checked,
      // Turning recurrence on drops any picked service (mutually exclusive).
      ...(checked ? { selectedServiceId: '', selectedServiceName: '' } : {}),
    }));
  };

  // Clicking a month chip means different things per frequency: monthly toggles
  // freely; quarterly/half-yearly RE-ANCHOR and re-derive the siblings; yearly
  // is a single-month anchor.
  const handleMonthChip = (monthNo) => {
    const freq = formData.recurrenceFrequency;
    if (freq === 'monthly') {
      setFormData((prev) => ({
        ...prev,
        recurrenceMonths: prev.recurrenceMonths.includes(monthNo)
          ? prev.recurrenceMonths.filter((m) => m !== monthNo)
          : [...prev.recurrenceMonths, monthNo],
      }));
      return;
    }
    if (freq === 'yearly') {
      setFormData((prev) => ({ ...prev, recurrenceAnchorMonth: monthNo, recurrenceMonths: [monthNo] }));
      return;
    }
    const step = ANCHOR_STEP[freq];
    if (step) {
      setFormData((prev) => ({
        ...prev,
        recurrenceAnchorMonth: monthNo,
        recurrenceMonths: anchoredMonths(monthNo, step),
      }));
    }
  };

  // Switching frequency has to re-normalise the month selection, since each
  // mode reads the chips differently (all-twelve vs. anchored vs. single).
  const handleFrequencyChange = (e) => {
    const freq = e.target.value;
    setFormData((prev) => {
      const anchor = prev.recurrenceAnchorMonth || CURRENT_MONTH;
      let recurrenceMonths = prev.recurrenceMonths;
      if (freq === 'monthly') recurrenceMonths = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
      else if (freq === 'yearly') recurrenceMonths = [anchor];
      else if (ANCHOR_STEP[freq]) recurrenceMonths = anchoredMonths(anchor, ANCHOR_STEP[freq]);
      return { ...prev, recurrenceFrequency: freq, recurrenceMonths, recurrenceAnchorMonth: anchor };
    });
  };

  const toggleRecurrenceWeek = (weekNo) => {
    setFormData((prev) => ({
      ...prev,
      recurrenceWeeks: prev.recurrenceWeeks.includes(weekNo)
        ? prev.recurrenceWeeks.filter((w) => w !== weekNo)
        : [...prev.recurrenceWeeks, weekNo],
    }));
  };

  const toggleRecurrenceYear = (year) => {
    setFormData((prev) => ({
      ...prev,
      recurrenceYears: prev.recurrenceYears.includes(year)
        ? prev.recurrenceYears.filter((y) => y !== year)
        : [...prev.recurrenceYears, year],
    }));
  };

  // Plain-English preview of the recurrence due-date rule, month-end aware.
  const recurrenceDuePreview = () => {
    const off = Number(formData.recurrenceMonthOffset) || 0;
    const offText =
      off === 0 ? 'of the period-end month' : off === 1 ? 'of the following month' : `, ${off} months after period end`;
    if (formData.recurrenceDueMode === 'none') {
      return 'No due date — each occurrence is left undated for staff to fill in.';
    }
    if (formData.recurrenceDueMode === 'month_end') {
      return `Due the last day ${offText} — resolves to 28, 29, 30 or 31 automatically per month (leap-year aware).`;
    }
    if (!formData.recurrenceDueDay) return 'Enter the day of the month.';
    const d = Number(formData.recurrenceDueDay);
    return `Due the ${ordinal(d)} ${offText}${d > 28 ? ' — clamped to the last day in shorter months.' : '.'}`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // A number input's min/max only bind the spinner + submit validation — typed
  // values still stick (you can key in "100" past max=31). Clamp on change so
  // the field can never hold an out-of-range value. Empty stays empty.
  const handleClampedNumber = (name, minV, maxV) => (e) => {
    const raw = e.target.value;
    if (raw === '') {
      setFormData((prev) => ({ ...prev, [name]: '' }));
      return;
    }
    let n = Math.floor(Number(raw));
    if (Number.isNaN(n)) return;
    if (minV != null) n = Math.max(minV, n);
    if (maxV != null) n = Math.min(maxV, n);
    setFormData((prev) => ({ ...prev, [name]: String(n) }));
  };

  const handleBillableToggle = (e) => {
    setFormData((prev) => ({ ...prev, isBillable: e.target.checked }));
  };

  const toggleClient = (clientId) => {
    setFormData((prev) => ({
      ...prev,
      clientIds: prev.clientIds.includes(clientId)
        ? prev.clientIds.filter((existingId) => existingId !== clientId)
        : [...prev.clientIds, clientId],
    }));
  };

  // Clients matching the search box (name or code). Selected ones always show,
  // even when they don't match the current filter, so a selection can't hide.
  const visibleClients = useMemo(() => {
    const term = clientSearch.trim().toLowerCase();
    if (!term) return clients;
    return clients.filter(
      (c) =>
        formData.clientIds.includes(c._id) ||
        c.displayName.toLowerCase().includes(term) ||
        (c.clientCode || '').toLowerCase().includes(term)
    );
  }, [clients, clientSearch, formData.clientIds]);

  const toggleAssistant = (userId) => {
    setFormData((prev) => ({
      ...prev,
      assistantIds: prev.assistantIds.includes(userId)
        ? prev.assistantIds.filter((existingId) => existingId !== userId)
        : [...prev.assistantIds, userId],
    }));
  };

  const setAssistantHours = (userId, hours) => {
    setFormData((prev) => ({
      ...prev,
      assistantHours: { ...prev.assistantHours, [userId]: hours },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.title.trim()) {
      setError('Task title is required');
      return;
    }

    if (formData.recurring) {
      if (formData.recurrenceEndType === 'on' && !formData.recurrenceEndDate) {
        setError('Pick an end date for the recurrence');
        return;
      }
      if (formData.recurrenceEndType === 'after' && !(Number(formData.recurrenceOccurrences) > 0)) {
        setError('Enter how many times it should repeat');
        return;
      }
      if (formData.recurrenceFrequency === 'weekly' && formData.recurrenceWeeks.length === 0) {
        setError('Select at least one week for the recurrence to run in');
        return;
      }
      if (formData.recurrenceFrequency === 'yearly' && formData.recurrenceYears.length === 0) {
        setError('Select at least one year for the recurrence to run in');
        return;
      }
      if (MONTH_BASED.includes(formData.recurrenceFrequency) && formData.recurrenceMonths.length === 0) {
        setError('Select at least one month for the recurrence to run in');
        return;
      }
      if (formData.recurrenceDueMode === 'day' && !(Number(formData.recurrenceDueDay) >= 1)) {
        setError('Enter the day of the month for the due date');
        return;
      }
    }

    setLoading(true);
    try {
      const hourAllocations = [];
      if (formData.preparerId && formData.preparerHours) {
        hourAllocations.push({
          userId: formData.preparerId,
          role: 'preparer',
          allocatedHours: Number(formData.preparerHours),
        });
      }
      if (formData.reviewerId && formData.reviewerHours) {
        hourAllocations.push({
          userId: formData.reviewerId,
          role: 'reviewer',
          allocatedHours: Number(formData.reviewerHours),
        });
      }
      formData.assistantIds.forEach((userId) => {
        const hours = formData.assistantHours[userId];
        if (hours) {
          hourAllocations.push({ userId, role: 'assistant', allocatedHours: Number(hours) });
        }
      });

      const payload = {
        title: formData.title,
        description: formData.description,
        priority: formData.priority,
        // A recurring task's date comes from the rule below — the server works
        // out this first occurrence's period and dates it accordingly. What's
        // typed here is the fallback for a schedule with no due-date rule
        // ("none"), which would otherwise leave the task undated and invisible
        // in every date-driven view.
        dueDate: formData.dueDate || undefined,
        tags: formData.tags,
        clientIds: formData.clientIds,
        // The catalog service this task is work against. Stored on the task so
        // it can be filtered and reported on — picking one used to change
        // nothing but the title text.
        serviceId: formData.selectedServiceId || undefined,
        preparerId: formData.preparerId || undefined,
        reviewerId: formData.reviewerId || undefined,
        assistantIds: formData.assistantIds,
        hourAllocations,
        isBillable: formData.isBillable,
        billType: formData.isBillable ? formData.billType : undefined,
        billAmount: formData.isBillable && formData.billAmount ? Number(formData.billAmount) : undefined,
        recurrence: formData.recurring
          ? {
              isRecurring: true,
              frequency: formData.recurrenceFrequency,
              interval: Number(formData.recurrenceInterval) || 1,
              months: MONTH_BASED.includes(formData.recurrenceFrequency)
                ? [...formData.recurrenceMonths].sort((a, b) => a - b)
                : [],
              weeks:
                formData.recurrenceFrequency === 'weekly'
                  ? [...formData.recurrenceWeeks].sort((a, b) => a - b)
                  : [],
              years:
                formData.recurrenceFrequency === 'yearly'
                  ? [...formData.recurrenceYears].sort((a, b) => a - b)
                  : [],
              endType: formData.recurrenceEndType,
              endDate: formData.recurrenceEndType === 'on' ? formData.recurrenceEndDate || null : null,
              occurrences:
                formData.recurrenceEndType === 'after'
                  ? Number(formData.recurrenceOccurrences) || null
                  : null,
              dueOnMonthEnd: formData.recurrenceDueMode === 'month_end',
              dueDay:
                formData.recurrenceDueMode === 'day' && formData.recurrenceDueDay
                  ? Number(formData.recurrenceDueDay)
                  : null,
              monthOffset:
                formData.recurrenceDueMode !== 'none' ? Number(formData.recurrenceMonthOffset) || 0 : 1,
            }
          : undefined,
      };

      const response = await apiClient.post('/api/tasks', payload);
      toast.success('Task created');
      navigate(`${backPath}/${response.data._id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create task');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout title="Create New Task" subtitle="Add a new task to your workflow">
      <Card className="create-task-card">
        <form onSubmit={handleSubmit} className="create-task-form">
          {error && <div className="form-error" role="alert">{error}</div>}

          <div className="create-task-grid">
            <div className="create-task-main">
              <Input
                label="Task Title *"
                id="title"
                name="title"
                placeholder="Enter task title"
                value={formData.title}
                onChange={handleChange}
                required
                disabled={loading}
              />

              {services.length > 0 && !formData.recurring && (
                <div className="service-picker-field">
                  <label className="ds-field-label">Or pick from Services (optional)</label>
                  {formData.selectedServiceId && (
                    <div className="selected-service-chip">
                      <span>Service: <strong>{formData.selectedServiceName}</strong></span>
                      <button type="button" onClick={clearSelectedService} disabled={loading} aria-label="Clear selected service">✕</button>
                    </div>
                  )}
                  <Input
                    ref={serviceInputRef}
                    className="service-picker-search"
                    placeholder="🔎 Search services…"
                    value={serviceSearch}
                    onChange={(e) => setServiceSearch(e.target.value)}
                    onFocus={() => setServicePickerOpen(true)}
                    disabled={loading}
                  />
                  {servicePickerOpen && (
                    <div className="service-picker-box" ref={serviceBoxRef}>
                      {serviceGroups.length === 0 ? (
                        <p className="empty-message">No services found</p>
                      ) : (
                        serviceGroups.map(({ displayName, items }) => (
                          <div key={displayName} className="service-picker-group">
                            <div className="service-picker-group-title">
                              {displayName} <span className="service-picker-group-count">({items.length})</span>
                            </div>
                            {items.map((s) => (
                              <button
                                key={s._id}
                                type="button"
                                className="service-picker-option"
                                onClick={() => handleServiceSelect(s)}
                                disabled={loading}
                              >
                                {formatServiceName(s.name)}
                              </button>
                            ))}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="ds-field">
                <label className="ds-field-label">Client(s) (optional) — link this task to one or more clients</label>
                <Input
                  className="service-picker-search"
                  placeholder="🔎 Search clients…"
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  disabled={loading}
                />
                <div className="assistants-checkbox-list">
                  {clients.length === 0 ? (
                    <p className="empty-message">No clients yet</p>
                  ) : visibleClients.length === 0 ? (
                    <p className="empty-message">No clients match “{clientSearch}”</p>
                  ) : (
                    visibleClients.map((c) => (
                      <label key={c._id} className="assistant-checkbox">
                        <input
                          type="checkbox"
                          checked={formData.clientIds.includes(c._id)}
                          onChange={() => toggleClient(c._id)}
                          disabled={loading}
                        />
                        {c.displayName}{c.clientCode ? ` (${c.clientCode})` : ''}
                      </label>
                    ))
                  )}
                </div>
                {formData.clientIds.length > 0 && (
                  <span className="service-picker-count">
                    {formData.clientIds.length} client{formData.clientIds.length === 1 ? '' : 's'} selected
                  </span>
                )}
              </div>

              <div className="ds-field">
                <label htmlFor="description" className="ds-field-label">Description</label>
                <textarea
                  id="description"
                  name="description"
                  className="ds-input"
                  placeholder="Enter task description"
                  rows="5"
                  value={formData.description}
                  onChange={handleChange}
                  disabled={loading}
                />
              </div>

              <div className="assistants-field">
                <label className="ds-field-label">
                  Assistants (optional) — juniors shadowing the preparer to help and learn. Set an hour
                  budget per person if you want to compare it against their actual logged hours later.
                </label>
                <div className="assistants-checkbox-list">
                  {orgUsers.map((u) => {
                    const checked = formData.assistantIds.includes(u._id);
                    return (
                      <div key={u._id} className="assistant-row">
                        <label className="assistant-checkbox">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleAssistant(u._id)}
                            disabled={loading}
                          />
                          {u.name}
                        </label>
                        {checked && (
                          <input
                            type="number"
                            className="ds-input assistant-hours-input"
                            min="0"
                            step="0.5"
                            placeholder="Hours"
                            value={formData.assistantHours[u._id] || ''}
                            onChange={(e) => setAssistantHours(u._id, e.target.value)}
                            disabled={loading}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="billing-field">
                <label className="billable-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.isBillable}
                    onChange={handleBillableToggle}
                    disabled={loading}
                  />
                  Billable
                </label>
                {formData.isBillable && (
                  <div className="form-row">
                    <Select
                      label="Bill Type"
                      id="billType"
                      name="billType"
                      value={formData.billType}
                      onChange={handleChange}
                      disabled={loading}
                    >
                      <option value="professional_fees">Professional Fees</option>
                      <option value="ope">Out-of-Pocket Expenses (OPE)</option>
                    </Select>
                    <Input
                      label="Amount (₹)"
                      type="number"
                      id="billAmount"
                      name="billAmount"
                      min="0"
                      step="0.01"
                      placeholder="Enter amount"
                      value={formData.billAmount}
                      onChange={handleChange}
                      disabled={loading}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="create-task-sidebar">
              <div className="form-row">
                <Select
                  label="Priority"
                  id="priority"
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  disabled={loading}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </Select>

                {/* A recurring task has no single due date — the recurrence
                    block at the bottom defines it per occurrence. */}
                {!formData.recurring && (
                  <Input
                    label="Due Date"
                    id="dueDate"
                    type="date"
                    name="dueDate"
                    value={formData.dueDate}
                    onChange={handleChange}
                    disabled={loading}
                  />
                )}
              </div>

              {/* There is no separate "Assigned To" picker any more. It wrote a
                  second owner into a field the compliance engine never set and
                  the approval workflow never read — so a task could name two
                  different people as its owner. The Preparer below IS the
                  owner. See src/utils/taskPeople.js. */}
              <div className="assignee-row">
                <Select
                  label="Preparer"
                  id="preparerId"
                  name="preparerId"
                  value={formData.preparerId}
                  onChange={handleChange}
                  disabled={loading}
                >
                  <option value="">-- None --</option>
                  {orgUsers.map((u) => (
                    <option key={u._id} value={u._id}>{u.name}</option>
                  ))}
                </Select>
                {formData.preparerId && (
                  <Input
                    label="Preparer Hours"
                    type="number"
                    name="preparerHours"
                    min="0"
                    step="0.5"
                    placeholder="Est. hours"
                    value={formData.preparerHours}
                    onChange={handleChange}
                    disabled={loading}
                  />
                )}
              </div>

              <div className="assignee-row">
                <Select
                  label="Reviewer"
                  id="reviewerId"
                  name="reviewerId"
                  value={formData.reviewerId}
                  onChange={handleChange}
                  disabled={loading}
                >
                  <option value="">-- None --</option>
                  {orgUsers.map((u) => (
                    <option key={u._id} value={u._id}>{u.name}</option>
                  ))}
                </Select>
                {formData.reviewerId && (
                  <Input
                    label="Reviewer Hours"
                    type="number"
                    name="reviewerHours"
                    min="0"
                    step="0.5"
                    placeholder="Est. hours"
                    value={formData.reviewerHours}
                    onChange={handleChange}
                    disabled={loading}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Recurring — full-width, at the bottom. Mutually exclusive with
              picking a Service above (a service already recurs via its own
              due-rule), so it's hidden the moment a service is selected. */}
          {!formData.selectedServiceId && (
            <div className="recurrence-field recurrence-bottom">
              <label className="billable-checkbox">
                <input
                  type="checkbox"
                  checked={formData.recurring}
                  onChange={handleRecurringToggle}
                  disabled={loading}
                />
                Recurring task
              </label>
              {formData.recurring && (
                <div className="recurrence-options">
                  <div className="form-row">
                    <Select
                      label="Repeats"
                      id="recurrenceFrequency"
                      name="recurrenceFrequency"
                      value={formData.recurrenceFrequency}
                      onChange={handleFrequencyChange}
                      disabled={loading}
                    >
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="half_yearly">Half-Yearly</option>
                      <option value="yearly">Yearly</option>
                    </Select>
                    <Input
                      label="Repeat every"
                      type="number"
                      id="recurrenceInterval"
                      name="recurrenceInterval"
                      min="1"
                      max="99"
                      step="1"
                      value={formData.recurrenceInterval}
                      onChange={handleClampedNumber('recurrenceInterval', 1, 99)}
                      disabled={loading}
                    />
                  </div>

                  {/* Weekly — which weeks of the month it fires in. */}
                  {formData.recurrenceFrequency === 'weekly' && (
                    <div className="recurrence-months">
                      <span className="ds-field-label">Run in these weeks</span>
                      <div className="month-chip-row">
                        {WEEK_LABELS.map(({ no, label }) => {
                          const on = formData.recurrenceWeeks.includes(no);
                          return (
                            <button
                              type="button"
                              key={no}
                              className={`month-chip ${on ? 'is-on' : ''}`}
                              onClick={() => toggleRecurrenceWeek(no)}
                              disabled={loading}
                              aria-pressed={on}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                      <p className="recurrence-hint">Pick any weeks of the month — "Last" is the final week even in months with a 5th.</p>
                    </div>
                  )}

                  {/* Monthly/quarterly/half-yearly/yearly — the month picker.
                      Monthly toggles freely; the others pick one anchor month
                      and derive (or, for yearly, just use) it. */}
                  {MONTH_BASED.includes(formData.recurrenceFrequency) && (
                    <div className="recurrence-months">
                      <span className="ds-field-label">
                        {formData.recurrenceFrequency === 'monthly' ? 'Run in these months' : 'Anchor month'}
                      </span>
                      <div className="month-chip-row">
                        {MONTHS.map((m, i) => {
                          const monthNo = i + 1;
                          const on = formData.recurrenceMonths.includes(monthNo);
                          const isAnchor =
                            formData.recurrenceFrequency !== 'monthly' &&
                            formData.recurrenceAnchorMonth === monthNo;
                          return (
                            <button
                              type="button"
                              key={m}
                              className={`month-chip ${on ? 'is-on' : ''} ${isAnchor ? 'is-anchor' : ''}`}
                              onClick={() => handleMonthChip(monthNo)}
                              disabled={loading}
                              aria-pressed={on}
                            >
                              {m}
                            </button>
                          );
                        })}
                      </div>
                      {formData.recurrenceFrequency === 'quarterly' && (
                        <p className="recurrence-hint">Pick one month — the other quarters fill in automatically, every 3 months.</p>
                      )}
                      {formData.recurrenceFrequency === 'half_yearly' && (
                        <p className="recurrence-hint">Pick one month — the second half-year fills in automatically, 6 months later.</p>
                      )}
                      {formData.recurrenceFrequency === 'yearly' && (
                        <p className="recurrence-hint">Pick the one month it runs each year.</p>
                      )}
                    </div>
                  )}

                  {/* Yearly — which specific years it should run in. */}
                  {formData.recurrenceFrequency === 'yearly' && (
                    <div className="recurrence-months">
                      <span className="ds-field-label">Run in these years</span>
                      <div className="month-chip-row">
                        {YEAR_OPTIONS.map((year) => {
                          const on = formData.recurrenceYears.includes(year);
                          return (
                            <button
                              type="button"
                              key={year}
                              className={`month-chip ${on ? 'is-on' : ''}`}
                              onClick={() => toggleRecurrenceYear(year)}
                              disabled={loading}
                              aria-pressed={on}
                            >
                              {year}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="form-row">
                    <Select
                      label="Ends"
                      id="recurrenceEndType"
                      name="recurrenceEndType"
                      value={formData.recurrenceEndType}
                      onChange={handleChange}
                      disabled={loading}
                    >
                      <option value="never">Never</option>
                      <option value="on">On date</option>
                      <option value="after">After N occurrences</option>
                    </Select>
                    {formData.recurrenceEndType === 'on' && (
                      <Input
                        label="End date"
                        type="date"
                        id="recurrenceEndDate"
                        name="recurrenceEndDate"
                        value={formData.recurrenceEndDate}
                        onChange={handleChange}
                        disabled={loading}
                      />
                    )}
                    {formData.recurrenceEndType === 'after' && (
                      <Input
                        label="Occurrences"
                        type="number"
                        id="recurrenceOccurrences"
                        name="recurrenceOccurrences"
                        min="1"
                        max="999"
                        step="1"
                        placeholder="e.g. 12"
                        value={formData.recurrenceOccurrences}
                        onChange={handleClampedNumber('recurrenceOccurrences', 1, 999)}
                        disabled={loading}
                      />
                    )}
                  </div>

                  {/* Due-date definition for each generated occurrence, relative
                      to its period. "Last day of month" resolves to 28/29/30/31
                      per month automatically. */}
                  <div className="recurrence-duerule">
                    <span className="ds-field-label">Due date for each occurrence</span>
                    <div className="form-row">
                      <Select
                        label="Due date"
                        id="recurrenceDueMode"
                        name="recurrenceDueMode"
                        value={formData.recurrenceDueMode}
                        onChange={handleChange}
                        disabled={loading}
                      >
                        <option value="none">Not set (undated)</option>
                        <option value="day">Specific day of month</option>
                        <option value="month_end">Last day of month</option>
                      </Select>
                      {formData.recurrenceDueMode === 'day' && (
                        <Input
                          label="Due on day"
                          type="number"
                          id="recurrenceDueDay"
                          name="recurrenceDueDay"
                          min="1"
                          max="31"
                          placeholder="1–31"
                          value={formData.recurrenceDueDay}
                          onChange={handleClampedNumber('recurrenceDueDay', 1, 31)}
                          disabled={loading}
                        />
                      )}
                      {formData.recurrenceDueMode !== 'none' && (
                        <Input
                          label="Months after period"
                          type="number"
                          id="recurrenceMonthOffset"
                          name="recurrenceMonthOffset"
                          min="0"
                          max="24"
                          step="1"
                          value={formData.recurrenceMonthOffset}
                          onChange={handleClampedNumber('recurrenceMonthOffset', 0, 24)}
                          disabled={loading}
                        />
                      )}
                    </div>
                    <p className="recurrence-duerule-preview">{recurrenceDuePreview()}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="form-actions">
            <Button type="submit" variant="primary" loading={loading}>
              {loading ? 'Creating…' : 'Create Task'}
            </Button>
            <Button type="button" variant="secondary" onClick={() => navigate(backPath)}>
              Cancel
            </Button>
          </div>
        </form>
      </Card>
    </Layout>
  );
}

export default CreateTask;
